import { HTMLAttributes } from 'react'

import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Motion from '@/components/base/Motion'
import ScrollingIcon from '@/components/base/ScrollingIcon'
import Typography from '@/components/base/Typography'
import { useStore } from '@/contexts/store'

import { heroAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPImage, WPPageData, WPVideo } from '@/types'

export type HeroProps = HTMLAttributes<HTMLElement> &
  Partial<{
    headline: string
    only_text: boolean
    poster: {
      desktop: WPImage
      mobile: WPImage
      hasVideo: boolean
    }
    video: {
      desktop: WPVideo
      mobile: WPVideo
    }
    theme: WPPageData['acf']['hero']['theme']
  }>

const HeroOnlyText = ({ headline, theme = 'light', className }: HeroProps) => {
  const { strings } = useStore()

  return (
    <Motion animation={heroAnimation}>
      {(motionRef) => (
        <section
          ref={motionRef}
          className={cn([
            'h-svh-vh w-full',
            {
              'bg-dark-600 text-white': theme === 'dark',
              'bg-dark-100 text-dark-500': theme === 'light',
              'bg-dark-500 text-white': theme === 'onyx'
            },
            className
          ])}
          data-hero
        >
          <div className="relative z-10 flex h-full w-full items-center overflow-hidden">
            <Container>
              <Grid>
                {headline ? (
                  <Typography
                    variant="h1"
                    component="h1"
                    className="col-span-4 text-center md:col-span-6 lg:col-span-10 lg:col-start-2"
                    data-motion-headline
                  >
                    {headline}
                  </Typography>
                ) : null}
              </Grid>
            </Container>
            <div
              className={cn([
                'absolute bottom-gutter left-gutter right-gutter z-10 lg:bottom-[6rem]',
                'flex flex-col justify-center gap-[6rem] lg:gap-0'
              ])}
              data-motion-bottom
            >
              <div className="flex items-end justify-between">
                <ScrollingIcon
                  className="lg:absolute lg:bottom-0 lg:left-0"
                  theme={theme}
                />
                <Typography
                  variant="label"
                  className="lg:absolute lg:bottom-0 lg:right-0"
                >
                  {strings?.bottom_right_string}
                </Typography>
              </div>
            </div>
          </div>
        </section>
      )}
    </Motion>
  )
}

export default HeroOnlyText
